import { Box, Button, Container, Stack, TextField, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Layout from "../../components/Layout";
import { PoweredByFooter } from "../../components/PoweredByFooter";
import { validEmailRegex } from "../../globals/helpers/validators";
import { useInvitation } from "../../hooks/useFormsLists";
import useToken from "../../hooks/useToken";
import { useAlert } from "../../lib/Alerts";
import { authHeader, formsFetch, unwrap } from "../../lib/formsApi";

const InvitationsQuestionnairePage: React.FC = () => {
    const { alertUser } = useAlert();
    const [email, setEmail] = useState("");
    const [touched, setTouched] = useState(false);

    const { formatMessage } = useIntl();

    const { decoded, token } = useToken();

    const { data: invitation } = useInvitation();

    const startReview = async () => {
        try {
            const { invitationUrl } = unwrap(
                await formsFetch.POST("/api/feedback-links", {
                    headers: authHeader(token),
                    body: { receiverEmail: email },
                }),
                "create feedback link"
            );
            window.location.href = invitationUrl;
        } catch (err) {
            Sentry.captureException(err, { extra: { email } });
            alertUser({
                value: <FormattedMessage defaultMessage="Failed to start review" />,
                severity: "error",
            });
        }
    };

    const isEmailValid = (email: string): boolean => {
        return email.trim() !== "" && validEmailRegex.test(email);
    };

    const getEmailError = (): string => {
        if (!email.trim()) {
            return formatMessage({ defaultMessage: "Email is required" });
        }
        if (!validEmailRegex.test(email)) {
            return formatMessage({ defaultMessage: "Please enter a valid email address" });
        }
        return "";
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (!isEmailValid(email)) return;

        void startReview();
    };

    const supplierName = invitation?.supplierName ?? "";
    const customerName = decoded.customerName ?? "";

    const appBarTitle = `${customerName} - ${formatMessage({ defaultMessage: "Supplier feedback" })}`;

    return (
        <Layout appBar={true} appBarTitle={appBarTitle}>
            <Container
                maxWidth="sm"
                sx={{ py: 8, display: "flex", flexDirection: "column", minHeight: "calc(100vh - 64px)" }}
            >
                <Stack spacing={3} sx={{ flex: 1 }}>
                    <Stack spacing={1}>
                        <Typography variant="textXl" fontWeight={500}>
                            <FormattedMessage defaultMessage="Performance review request - identification needed" />
                        </Typography>
                        <Stack>
                            <Typography variant="textMd">
                                <FormattedMessage
                                    defaultMessage="{initiator} requests an internal performance review for {supplier}."
                                    values={{
                                        initiator: customerName,
                                        supplier: supplierName ? `${supplierName}` : "[unknown supplier]",
                                    }}
                                />
                            </Typography>
                            <Typography variant="textMd">
                                <FormattedMessage defaultMessage="Please provide your email to get started and contribute feedback." />
                            </Typography>
                        </Stack>
                    </Stack>

                    <Box component="form">
                        <Stack spacing={2}>
                            <TextField
                                label={<FormattedMessage defaultMessage="Your email" />}
                                placeholder={formatMessage({ defaultMessage: "Enter your email" })}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                error={touched && !isEmailValid(email)}
                                helperText={touched ? getEmailError() : ""}
                                fullWidth
                                size="medium"
                                type="email"
                            />
                            <Stack direction="row" justifyContent="flex-start">
                                <Button type="submit" variant="contained" size="small" onClick={handleSubmit}>
                                    <FormattedMessage defaultMessage="Start review" />
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>
                <Stack py={4}>
                    <PoweredByFooter />
                </Stack>
            </Container>
        </Layout>
    );
};

export default InvitationsQuestionnairePage;
